/**
 * Keyboard Wedge Scanner Service
 * 
 * This service listens for rapid keyboard input that indicates a barcode scanner
 * is typing data. Most barcode scanners in keyboard wedge mode type very fast
 * (usually completing within 50-100ms) and end with Enter or Tab.
 */

import { EventEmitter } from 'events';

export class KeyboardWedgeScanner extends EventEmitter {
  private buffer: string = '';
  private lastKeyTime: number = 0;
  private readonly SCAN_TIMEOUT = 100; // ms between keys to consider it a scan
  private readonly MIN_SCAN_LENGTH = 3; // minimum characters for a valid scan
  private isListening: boolean = false;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    super();
  }

  /**
   * Check if scanner debug logging should be enabled
   * Only logs when in development mode AND hardware widget is visible
   */
  private shouldLog(): boolean {
    if (process.env.NODE_ENV !== 'development') {
      return false;
    }

    // Check if hardware widget is visible in localStorage
    try {
      const widgetVisible = localStorage.getItem('hardware_widget_visible');
      return widgetVisible === 'true';
    } catch (error) {
      // If localStorage is not available, default to true in development
      return true;
    }
  }

  /**
   * Conditional console.log - only logs when shouldLog() returns true
   */
  private debugLog(...args: any[]): void {
    if (this.shouldLog()) {
      console.log(...args);
    }
  }

  /**
   * Conditional console.warn - only logs when shouldLog() returns true
   */
  private debugWarn(...args: any[]): void {
    if (this.shouldLog()) {
      console.warn(...args);
    }
  }

  /**
   * Conditional console.error - only logs when shouldLog() returns true
   */
  private debugError(...args: any[]): void {
    if (this.shouldLog()) {
      console.error(...args);
    }
  }

  /**
   * Start listening for keyboard wedge scanner input
   */
  start(): void {
    if (this.isListening) return;
    
    this.debugLog('[KeyboardWedgeScanner] Starting keyboard wedge listener');
    
    this.keyHandler = this.handleKeyPress.bind(this);
    document.addEventListener('keypress', this.keyHandler, true);
    document.addEventListener('keydown', this.handleKeyDown.bind(this), true);
    
    this.isListening = true;
  }

  /**
   * Stop listening for scanner input
   */
  stop(): void {
    if (!this.isListening) return;
    
    if (this.keyHandler) {
      document.removeEventListener('keypress', this.keyHandler, true);
      document.removeEventListener('keydown', this.handleKeyDown.bind(this), true);
    }
    
    this.isListening = false;
    this.buffer = '';
  }

  /**
   * Handle keydown for special keys (Enter, Tab)
   */
  private handleKeyDown(event: KeyboardEvent): void {
    const now = Date.now();
    
    // Check if this might be part of a scan
    if (now - this.lastKeyTime < this.SCAN_TIMEOUT) {
      // Handle Enter or Tab as scan terminator
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        this.processScan();
      }
    }
  }

  /**
   * Handle regular character input
   */
  private handleKeyPress(event: KeyboardEvent): void {
    // Ignore if user is typing in an input field (unless it's our hidden scanner field)
    const target = event.target as HTMLElement;
    if (target && (
      target.tagName === 'INPUT' || 
      target.tagName === 'TEXTAREA' || 
      target.contentEditable === 'true'
    )) {
      // Allow normal typing in input fields
      return;
    }
    
    const now = Date.now();
    
    // If too much time has passed, reset the buffer
    if (now - this.lastKeyTime > this.SCAN_TIMEOUT) {
      this.buffer = '';
    }
    
    this.lastKeyTime = now;
    
    // Add character to buffer
    const char = event.key;
    if (char && char.length === 1) {
      this.buffer += char;
      
      // Prevent the character from being typed elsewhere
      event.preventDefault();
      event.stopPropagation();
      
      // Some scanners don't send Enter, so check for common patterns
      this.checkForCompleteBarcode();
    }
  }

  /**
   * Check if buffer contains a complete barcode
   */
  private checkForCompleteBarcode(): void {
    // Check for common barcode patterns
    
    // Pattern 1: Asterisk delimited (e.g., *12345*)
    if (this.buffer.startsWith('*') && this.buffer.endsWith('*') && this.buffer.length > 2) {
      this.processScan();
      return;
    }
    
    // Pattern 2: Pure numeric of reasonable length
    if (/^\d{5,20}$/.test(this.buffer)) {
      // Set a short timeout to see if more characters are coming
      setTimeout(() => {
        if (Date.now() - this.lastKeyTime >= this.SCAN_TIMEOUT) {
          this.processScan();
        }
      }, this.SCAN_TIMEOUT);
    }
  }

  /**
   * Process the scanned barcode
   */
  private processScan(): void {
    const barcode = this.buffer.trim();
    this.buffer = '';

    if (barcode.length < this.MIN_SCAN_LENGTH) {
      return;
    }

    this.debugLog('[KeyboardWedgeScanner] Barcode scanned:', barcode);

    // Emit the scan event - ScannerEventService is the single handler for all scan routing
    // (pick tickets, package QR codes, generic barcodes). No direct handling here to avoid
    // duplicate dispatches (e.g., double openShippingDialog calls).
    this.debugLog('[KeyboardWedgeScanner] Emitting scan event for ScannerEventService to route:', barcode);
    this.emit('scan', barcode);
  }

  /**
   * Test with manual input
   */
  testScan(barcode: string): void {
    this.debugLog('[KeyboardWedgeScanner] Manual test scan:', barcode);
    this.buffer = barcode;
    this.processScan();
  }
}

// Singleton instance
let wedgeScanner: KeyboardWedgeScanner | null = null;

export function getKeyboardWedgeScanner(): KeyboardWedgeScanner {
  if (!wedgeScanner) {
    wedgeScanner = new KeyboardWedgeScanner();
  }
  return wedgeScanner;
}